import { useTranslation } from 'react-i18next';
import { Alert, Stack, Typography } from '@mui/material';
import { ProgressField } from '../../ProgressField';
import { ProgramWizardOperatingMode, ProgramWizardStateData } from '../ProgramWizard.types';
import { isShutdownFinalWaitComplete, isTransitionComplete } from '../ProgramWizard.utils';

type WizardData = { operatingMode: ProgramWizardOperatingMode | null };

function programProgressLabelValuePairs({
  stateData,
  wizardData,
  t,
  isMobile,
}: {
  stateData: ProgramWizardStateData;
  wizardData: WizardData;
  t: (key: string) => string;
  isMobile: boolean;
}) {
  const shouldShowConnectedInput = [ProgramWizardOperatingMode.GRID, ProgramWizardOperatingMode.LOADSHARE].includes(
    wizardData.operatingMode as ProgramWizardOperatingMode
  );

  return (
    <>
      <Stack
        data-testid="progress-list"
        direction={isMobile ? 'column' : 'row'}
        spacing={isMobile ? 2 : 0}
        p={2}
        width="100%"
        justifyContent="space-between"
        bgcolor={(theme) => theme.palette.background.lightGrey}
      >
        <ProgressField title={t('starting.resettingSystem')} inProgress={stateData.resetInProgress} />
        <ProgressField title={t('starting.startingBatteries')} inProgress={stateData.bmsStarting} />
        <ProgressField title={t('starting.startingInverter')} inProgress={stateData.converterStarting} />
        {shouldShowConnectedInput && (
          <ProgressField title={t('starting.connectingInput')} inProgress={!stateData.inputConnected} />
        )}
      </Stack>
    </>
  );
}

function transitionProgressLabelValuePairs({
  stateData,
  operatingMode,
  transitionWaitComplete,
  shutdownCompletedAt,
  now,
  transitionCompleted,
  t,
  labels,
  isMobile,
}: {
  stateData: ProgramWizardStateData;
  operatingMode: ProgramWizardOperatingMode;
  transitionWaitComplete: boolean;
  shutdownCompletedAt: number | null;
  now: number;
  transitionCompleted: boolean;
  t: (key: string) => string;
  labels: { step1: string; step2: string; step3: string; step4: string; step5?: string };
  isMobile: boolean;
}) {
  // Once the transition has fully completed, pin the finished state so the progress UI cannot
  // regress after the wizard stops tracking the live transition.
  const isComplete =
    transitionCompleted ||
    isTransitionComplete({
      operatingMode,
      stateData,
      waitComplete: transitionWaitComplete,
      shutdownCompletedAt,
      now,
    });
  const phase1Done = transitionWaitComplete || isComplete;
  const inputDisconnected = !stateData.inputConnected;
  const converterOff = !stateData.converterStarted;

  const step1Started = true;
  const step2Started = true;
  const step3Started = phase1Done && inputDisconnected;
  const step4Started = phase1Done && inputDisconnected && converterOff;

  const steps = [
    {
      title: t(labels.step1),
      started: step1Started,
      inProgress: !phase1Done,
    },
    {
      title: t(labels.step2),
      started: step2Started,
      inProgress: stateData.inputConnected,
    },
    {
      title: t(labels.step3),
      started: step3Started,
      inProgress: phase1Done && inputDisconnected && stateData.converterStarted,
    },
    {
      title: t(labels.step4),
      started: step4Started,
      inProgress: operatingMode === ProgramWizardOperatingMode.STANDBY ? stateData.systemOff : !stateData.systemOff,
    },
  ];

  if (operatingMode === ProgramWizardOperatingMode.SHUTDOWN && labels.step5) {
    const step5Started = stateData.systemOff && shutdownCompletedAt !== null;
    steps.push({
      title: t(labels.step5),
      started: step5Started,
      inProgress: shutdownCompletedAt !== null && !isShutdownFinalWaitComplete({ shutdownCompletedAt, now }),
    });
  }

  const displaySteps = isComplete ? steps.map((step) => ({ ...step, started: true, inProgress: false })) : steps;

  return (
    <>
      <Stack
        data-testid="progress-list"
        direction={isMobile ? 'column' : 'row'}
        spacing={isMobile ? 2 : 0}
        p={2}
        width="100%"
        justifyContent="space-between"
        bgcolor={(theme) => theme.palette.background.lightGrey}
      >
        {displaySteps.map((step) => (
          <ProgressField key={step.title} title={step.title} inProgress={step.inProgress} started={step.started} />
        ))}
      </Stack>
    </>
  );
}

const STANDBY_LABELS = {
  step1: 'shutdownProgress.settingStandby',
  step2: 'starting.disconnectingInput',
  step3: 'starting.turningOffESI',
  step4: 'starting.turningOnBatteries',
};

const SHUTDOWN_LABELS = {
  step1: 'shutdownProgress.settingStandby',
  step2: 'shutdownProgress.disconnectingInput',
  step3: 'starting.turningOffESI',
  step4: 'shutdownProgress.turningOffBatteries',
  step5: 'shutdownProgress.shutdown',
};

const isStandbyProgram = (wizardData: WizardData) => wizardData.operatingMode === ProgramWizardOperatingMode.STANDBY;
const isShutdownProgram = (wizardData: WizardData) => wizardData.operatingMode === ProgramWizardOperatingMode.SHUTDOWN;

export default function StepProgramStarting({
  stateData,
  wizardData,
  isMobile = false,
  transitionWaitComplete = false,
  shutdownCompletedAt = null,
  now = Date.now(),
  transitionCompleted = false,
}: {
  stateData: ProgramWizardStateData;
  wizardData: WizardData;
  isMobile?: boolean;
  transitionWaitComplete?: boolean;
  shutdownCompletedAt?: number | null;
  now?: number;
  transitionCompleted?: boolean;
}) {
  const { t } = useTranslation('programWizard');
  const isStandBySelected = Boolean(stateData) && isStandbyProgram(wizardData);
  const isShutdownSelected = Boolean(stateData) && isShutdownProgram(wizardData);

  const titleText = isShutdownSelected
    ? t('shutdownProgress.inProgress')
    : isStandBySelected
      ? t('starting.transitioningStandby')
      : t('starting.batteryStarting');

  return (
    <>
      <Typography variant="body1">{titleText}</Typography>
      <Alert severity="info">{t('closingWindowNote')}</Alert>

      {isShutdownSelected
        ? transitionProgressLabelValuePairs({
            stateData,
            operatingMode: ProgramWizardOperatingMode.SHUTDOWN,
            transitionWaitComplete,
            shutdownCompletedAt,
            now,
            transitionCompleted,
            t,
            labels: SHUTDOWN_LABELS,
            isMobile,
          })
        : isStandBySelected
          ? transitionProgressLabelValuePairs({
              stateData,
              operatingMode: ProgramWizardOperatingMode.STANDBY,
              transitionWaitComplete,
              shutdownCompletedAt,
              now,
              transitionCompleted,
              t,
              labels: STANDBY_LABELS,
              isMobile,
            })
          : programProgressLabelValuePairs({ stateData, wizardData, t, isMobile })}
    </>
  );
}
